import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnFiltersState,
  type RowSelectionState,
  ColumnDef,
} from "@tanstack/react-table"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import {
  ArrowUpDown,
  Search,
  ChevronDown,
  ChevronRight,
  DollarSign,
  PlusCircle,
  PenTool,
  AlertCircle,
  Info,
  Loader2,
} from "lucide-react"
import useTranslator from "@pma/hooks/use-translator"
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { DictType } from "@pma/types"
import { buildBoQHierarchicalData, cn, exportToExcel, getFinancialErrorMessage } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { Checkbox } from "@components/common/checkbox"
import RequestAdditionalAmount from "./request-additional-amount"
import AddNewBoQItem from "./add-new-boq-item"
import ApproveBoqItems from "./approve-boq-items"
import { createPortal } from "react-dom"
import { toast } from "@pma/hooks/use-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import { BILL_CREATE_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import AccumulatedBillAmounts from "./accumulated-bill-amounts"
import { Label } from "@components/common/label"
import { Switch } from "@components/common/switch"
import SelectWorkDiaryList from "./select-work-diary-list"
import { Badge } from "../common/badge"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

interface Props {
  boqId: string
  data: any[]
  revalidate: CallableFunction
  context: "boq" | "bill-preview" | "bill-create" | "bill-review" | "bill-print"
  status?: any
  triggerApi?: boolean
  showStatus?: boolean
  showCurrentBill?: boolean
  setIsDisable?: Dispatch<SetStateAction<boolean>>
  setTriggerApi?: Dispatch<SetStateAction<boolean>>
  setChangeButtonColor?: Dispatch<SetStateAction<boolean>>
}

export default function BoQTable({
  boqId,
  data,
  revalidate,
  context = "boq",
  showStatus = false,
  showCurrentBill = false,
  status,
  setIsDisable,
  setTriggerApi,
  setChangeButtonColor,
  triggerApi,
}: Props) {
  const queryClient = useQueryClient()
  const [showDiscountedPrices, setShowDiscountedPrices] = useState(false)
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(""))
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showAddItemDialog, setShowAddItemDialog] = useState(false)
  const [showAdditionalAmountDialog, setShowAdditionalAmountDialog] = useState(false)
  const [selectedItemForAdditional, setSelectedItemForAdditional] = useState<DictType>({})
  const [billAmounts, setBillAmounts] = useState<Record<string, number>>({})
  const [discountAmounts, setDiscountAmounts] = useState<Record<string, number>>({})
  const [excludeDiscountEnabled, setExcludeDiscountEnabled] = useState<Record<string, boolean>>({})
  const [stateContext, setStateContext] = useState(context)
  const [selectedDairyId, setSelectedDiaryId] = useState<any>(null)
  const [activeInputId, setActiveInputId] = useState<string | null>(null)
  const [activeDiscountInputId, setActiveDiscountInputId] = useState<string | null>(null)

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const navigate = useNavigate()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { translate, locale } = useTranslator()
  const { userRoleDetails } = useContext(UserRoleContext)

  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const canRequestAdditional =
    userRoleDetails?.supervisor ||
    userRoleDetails?.fin_manager ||
    userRoleDetails?.constructor ||
    userRoleDetails?.designer

  const hierarchicalData = useMemo(() => buildBoQHierarchicalData(data), [data])

  const isRowVisible = (item: DictType): boolean => {
    if (item.level === 0) return true

    const codeParts = item?.number.split(".")

    for (let i = 1; i <= item.level; i++) {
      const parentCode = codeParts.slice(0, i).join(".")
      if (!expandedRows.has(parentCode)) {
        return false
      }
    }
    return true
  }

  const visibleData = useMemo(() => {
    return hierarchicalData.filter(isRowVisible)
  }, [hierarchicalData, expandedRows])

  const toggleExpanded = (number: string) => {
    const newExpanded = new Set(expandedRows)

    if (newExpanded.has(number)) {
      newExpanded.delete(number)

      hierarchicalData.forEach((item) => {
        if (item?.number?.startsWith(number + ".")) {
          newExpanded.delete(item.number)
        }
      })
    } else {
      newExpanded.add(number)
    }
    setExpandedRows(newExpanded)
  }

  const handleRowSelection = (rowId: string, isSelected: boolean) => {
    const newSelection = { ...rowSelection }
    const item = hierarchicalData.find((item) => item.id == rowId)

    if (!item) return

    if (isSelected) {
      newSelection[rowId] = true

      hierarchicalData.forEach((child) => {
        if (child?.number.startsWith(item?.number + ".")) {
          newSelection[child.id] = true
        }
      })
    } else {
      delete newSelection[rowId]

      hierarchicalData.forEach((child) => {
        if (child?.number.startsWith(item?.number + ".")) {
          delete newSelection[child.id]
        }
      })

      if (item.parentCode) {
        const siblings = hierarchicalData.filter(
          (sibling) => sibling.parentCode === item.parentCode,
        )
        const hasSelectedSibling = siblings.some((sibling) => newSelection[sibling.id])

        if (!hasSelectedSibling) {
          const parent = hierarchicalData.find((parent) => parent?.number === item.parentCode)
          if (parent) {
            delete newSelection[parent.id]
          }
        }
      }
    }

    setRowSelection(newSelection)
  }

  const billTotal = () => {
    const total = data
      .filter((item) => billAmounts[item?.id])
      .reduce((total, item) => total + Number(billAmounts[item?.id]) * item?.discount_price, 0)

    return formatCurrency(total, isHebrew)
  }

  const addBill = async () => {
    const updatedItems = data
      .map((item) => {
        if (item?.is_item && Number(billAmounts[item?.id])) {
          return {
            item_id: item?.id,
            amount: Number(billAmounts[item?.id]),
            discount: item?.id in discountAmounts ? discountAmounts[item?.id] : null,
          }
        }
        return
      })
      .filter(Boolean)

    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILL_CREATE_ENDPOINT,
      {
        boq_id: boqId,
        items: updatedItems,
        ...(selectedDairyId ? { diary_id: selectedDairyId } : {}),
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )

    return response.data
  }

  const addBillMutation = useMutation({
    mutationKey: ["new-bill"],
    mutationFn: addBill,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("bill created successfully") + " — " + translate("moved to supervisor approval"),
        variant: "success",
      })
      newParams.set("revalidate", "bills")
      newParams.set("bill_tab", "all")
      navigate(`?${newParams.toString()}`)
      revalidate && revalidate()
      queryClient.invalidateQueries({ queryKey: ["all-bills-summary"] })
      queryClient.invalidateQueries({ queryKey: ["main-boq-details"] })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "create bill", translate),
        variant: "error",
      })
    },
  })

  const columns = useMemo((): ColumnDef<any>[] => {
    const baseColumns: ColumnDef<any>[] = [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            disabled={["bill-print", "bill-create", "bill-review"].includes(stateContext)}
            onCheckedChange={(value) => {
              if (value) {
                const newSelection = { ...rowSelection }
                visibleData.forEach((row) => {
                  if (row.is_item) {
                    newSelection[row.id] = true
                    hierarchicalData.forEach((child) => {
                      if (child?.number.startsWith(row?.number + ".")) {
                        newSelection[child.id] = true
                      }
                    })
                  }
                })
                setRowSelection(newSelection)
              } else setRowSelection({})
            }}
            aria-label='Select all'
            className='mr-2'
          />
        ),
        cell: ({ row }) =>
          row?.original?.is_item ? (
            <Checkbox
              checked={!!rowSelection[row.original.id]}
              onCheckedChange={(value) => handleRowSelection(row.original.id, !!value)}
              aria-label='Select row'
              disabled={["bill-print", "bill-create", "bill-review"].includes(stateContext)}
              className='mr-2'
            />
          ) : (
            <></>
          ),
        size: 40,
      },
      {
        accessorKey: "number",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Section')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemNumber = row?.original?.number
          const isExpanded = expandedRows.has(itemNumber)
          const parentCode = String(row.original.parentCode || "1")
          const indentationPx = parentCode.split(".").length * 20
          return (
            <div
              className={cn("flex gap-1")}
              style={{
                paddingRight: isHebrew ? `${indentationPx}px` : undefined,
                paddingLeft: !isHebrew ? `${indentationPx}px` : undefined,
              }}
            >
              {row?.original.hasChildren && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-5 w-5 p-0'
                  onClick={() => toggleExpanded(itemNumber)}
                >
                  {isExpanded ? (
                    <ChevronDown className='h-4 w-4' />
                  ) : (
                    <ChevronRight className='h-4 w-4' />
                  )}
                </Button>
              )}
              <span className='text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Description')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemDescription = row?.original?.description
          return (
            <div className='max-w-[200px] text-right text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[250px]'>
              {itemDescription}
            </div>
          )
        },
        enableSorting: false,
      },
      ...(showStatus
        ? [
            {
              accessorKey: "status",
              header: ({ column }: any) => <DataTableColumnHeader column={column} title={translate('Status')} />,
              cell: ({ row }: any) => {
                const bill = row?.original.bill

                if (bill?.amount_by_signer) {
                  return (
                    <Badge variant='secondary' className='flex min-w-[100px] justify-center gap-1'>
                      <PenTool className='h-3 w-3' /> {translate("Signed")}
                    </Badge>
                  )
                }

                if (
                  bill?.billStatus === "declined" ||
                  (bill?.billStatus === "paid" && bill?.amount_by_signer < 1) ||
                  bill?.is_declined ||
                  bill?.declined
                ) {
                  return (
                    <Badge
                      variant='destructive'
                      className='flex min-w-[100px] justify-center gap-1'
                    >
                      <AlertCircle className='h-3 w-3' /> {translate("Declined")}
                    </Badge>
                  )
                }

                if (bill?.approved_amount >= bill?.amount) {
                  return (
                    <Badge variant='secondary' className='flex min-w-[100px] justify-center'>
                      {translate("Approved")}
                    </Badge>
                  )
                }

                if (bill?.approved_amount > 0) {
                  return (
                    <Badge variant='default' className='flex min-w-[100px] justify-center gap-1'>
                      <Info className='h-3 w-3' /> {translate("Partially Approved")}
                    </Badge>
                  )
                }

                return (
                  <Badge variant='disabled' className='flex min-w-[100px] justify-center gap-1'>
                    <Loader2 className='h-3 w-3 animate-spin' /> {translate("Pending")}
                  </Badge>
                )
              },
            },
          ]
        : []),
      {
        accessorKey: "units",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
        ),
        cell: ({ row }: any) => {
          const itemUnit = row?.original?.units
          return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
        },
        enableSorting: false,
      },
      {
        accessorKey: "boq_cost",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Price per unit')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='min-w-fit text-center text-xs sm:text-sm'>
              {item?.is_item && (
                <Tooltip>
                  <TooltipTrigger>
                    <span className='flex items-center gap-1'>
                      {formatCurrency(item?.discount_price, isHebrew)}
                      <small className='text-[10px]'>
                        ({Number(((item?.item_discount ?? item?.total_discount ?? 0) * 100).toFixed(2))}%)
                      </small>
                    </span>{" "}
                    {showDiscountedPrices && (item?.item_discount ?? item?.total_discount) ? (
                      <span className='text-xs text-slate-500 line-through sm:text-sm'>
                        {formatCurrency(item?.boq_cost, isHebrew)}
                      </span>
                    ) : (
                      <></>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {translate("Discount applied to price")}:{" "}
                    {Number(((item?.item_discount ?? item?.total_discount ?? 0) * 100).toFixed(2))}%
                  </TooltipContent>
                </Tooltip>
              )}
              {item?.is_item && stateContext != "bill-create" && (
                <div className='mt-1 flex items-center justify-center gap-1'>
                  <Label className='text-[10px]'>
                    {translate("Exclude Discount")}:{" "}
                    {item?.bill?.item_discount === 0 ? translate("True") : translate("False")}
                  </Label>
                </div>
              )}
            </div>
          )
        },
        size: 150,
        enableSorting: false,
      },
      ...(stateContext === "bill-create"
        ? [
            {
              accessorKey: "exclude_discount",
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} title={translate('Exclude Discount')} />
              ),
              cell: ({ row }: any) => {
                const item = row?.original
                return (
                  <div className='flex min-w-[120px] flex-col items-center gap-1 text-center text-xs sm:min-w-fit sm:text-sm'>
                    {item?.is_item && (
                      <Switch
                        checked={!!excludeDiscountEnabled[item?.id]}
                        onCheckedChange={(checked) => {
                          setExcludeDiscountEnabled((prev) => ({ ...prev, [item?.id]: !!checked }))
                          if (checked) {
                            setDiscountAmounts((prev) => ({ ...prev, [item?.id]: 0 }))
                          } else {
                            setDiscountAmounts((prev) => {
                              const { [item?.id]: _, ...rest } = prev
                              return rest
                            })
                          }
                        }}
                      />
                    )}
                  </div>
                )
              },
              size: 100,
              enableSorting: false,
              enableHiding: true,
            },
          ]
        : []),
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount from estimate')} className='' />
        ),
        cell: ({ row }: any) => {
          return (
            <div className='max-w-xs text-right text-xs leading-tight sm:text-sm'>
              {row?.original?.amount || ""}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
      },
      {
        accessorKey: "paid",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Amount')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          const bill = row?.original.bill
          let accumulatedAmount = 0
          if (bill) {
            if (["new", "pending"].includes(bill.fullBillStatus) && item?.is_item) {
              accumulatedAmount = (item?.bill?.amount || 0) + (Number(item?.bill?._boq_item?.paid) || 0)
            } else if (bill.fullBillStatus === "approved" && item?.is_item) {
              accumulatedAmount = (item?.bill?.approved_amount || 0) + (Number(item?.bill?._boq_item?.paid) || 0)
            } else if (bill.fullBillStatus === "signed" && item?.is_item) {
              accumulatedAmount = item?.paid || item?.bill?._boq_item?.paid || 0
            }
          }

          return stateContext == "bill-print" ? (
            <>0</>
          ) : (
            <div className='text-center text-xs sm:text-sm'>
              {item?.is_item ? (
                !item?.bill ? (
                  <span>—</span>
                ) : (
                  <AccumulatedBillAmounts bill={item?.bill?._boq_item}>{accumulatedAmount}</AccumulatedBillAmounts>
                )
              ) : (
                <></>
              )}
            </div>
          )
        },
        size: 50,
        enableSorting: false,
      },
    ]

    if (["bill-print", "bill-create", "bill-review"].includes(stateContext)) {
      baseColumns.push({
        accessorKey: "current_bill",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Current Bill')} />,
        cell: ({ row }: any) => {
          const item = row?.original
          return item?.is_item ? (
            <div className='flex min-w-[120px] flex-col text-center text-xs sm:min-w-fit sm:text-sm'>
              {item?.is_item && !["bill-review", "bill-print"].includes(stateContext) && (
                <BillAmountInput
                  item={item}
                  billAmounts={billAmounts}
                  setBillAmounts={setBillAmounts}
                  activeInputId={activeInputId}
                  setActiveInputId={setActiveInputId}
                />
              )}
              {item?.is_item &&
                !["bill-review", "bill-print"].includes(stateContext) &&
                item?.bill && (
                  <>
                    <span className='text-xs text-primary'>
                      {translate("Requested")}: {item?.bill?.amount}
                    </span>
                    <span className='text-xs text-green-600'>
                      {translate("Bill Approved")}: {item?.bill?.approved_amount}
                    </span>
                  </>
                )}
              {showStatus ? (
                <>
                  <span className='text-xs text-primary'>
                    {translate("Requested")}: {item?.bill?.amount}
                  </span>
                  <span className='text-xs text-green-600'>
                    {translate("Bill Approved")}: {item?.bill?.approved_amount}
                  </span>
                </>
              ) : null}

              {showCurrentBill ? (
                <>
                  <span className='text-xs text-primary'>
                    {translate("Requested")}: {item?.bill?.amount || item?.amount || 0}
                  </span>
                  <span className='text-xs text-green-600'>
                    {translate("Bill Approved")}: {item?.bill?.approved_amount || item?.paid || 0}
                  </span>
                </>
              ) : null}
            </div>
          ) : null
        },
        size: 100,
        enableSorting: false,
        enableHiding: true,
      })
    }

    if (["bill-print", "bill-create", "bill-review"].includes(stateContext)) {
      baseColumns.push({
        accessorKey: "current_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Current Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return item?.is_item ? (
            <div className='text-center text-xs font-bold sm:text-sm'>
              {showStatus ? (
                <div className='flex flex-col items-center gap-1'>
                  <span className='text-xs'>
                    {translate("Requested")}:{" "}
                    {formatCurrency(item?.bill?.requested_sum ?? item?.discount_price * item?.bill?.amount, isHebrew)}
                  </span>
                  <span className='text-xs text-green-600'>
                    {translate(item?.bill?.is_signed ? "Signed" : "Approved")}:{" "}
                    {formatCurrency(item?.bill?.approved_sum ?? item?.discount_price * item?.bill?.approved_amount, isHebrew)}
                  </span>
                </div>
              ) : showCurrentBill ? (
                <div className='flex flex-col items-center gap-1'>
                  <span className='text-xs'>
                    {translate("Requested")}:{" "}
                    {item?.requested_sum
                      ? formatCurrency(item?.requested_sum, isHebrew)
                      : formatCurrency(
                          item?.discount_price * (item?.bill?.amount || item?.amount || 0),
                          isHebrew,
                        )}
                  </span>
                  <span className='text-xs text-green-600'>
                    {translate(item?.is_signed ? "Signed" : "Approved")}:{" "}
                    {item?.approved_sum
                      ? formatCurrency(item?.approved_sum, isHebrew)
                      : formatCurrency(
                          item?.discount_price * (item?.bill?.approved_amount || item?.paid || 0),
                          isHebrew,
                        )}
                  </span>
                </div>
              ) : item?.is_item ? (
                <AccumulatedTotalCell currentSum preview item={item} billAmounts={billAmounts} />
              ) : null}
            </div>
          ) : null
        },
        size: 200,
        enableSorting: false,
      })
    }

    baseColumns.push(
      {
        accessorKey: "estimated_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Boq sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return item?.is_item ? (
            <div className='text-center text-xs font-bold sm:text-sm'>
              <span className=''>{formatCurrency(item?.real_sum, isHebrew)}</span>
            </div>
          ) : null
        },
        enableSorting: false,
      },
      {
        accessorKey: "sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          const bill = row?.original.bill

          const accumulatedAmount = useMemo(() => {
            let totalAmount = 0
            if (bill) {
              if (["new", "pending"].includes(bill.fullBillStatus) && item?.is_item) {
                totalAmount += (item?.bill?.requested_sum || 0) + (Number(item?.bill?._boq_item?.paid_sum) || 0)
              } else if (bill.fullBillStatus === "approved" && item?.is_item) {
                totalAmount += (item?.bill?.approved_sum || 0) + (Number(item?.bill?._boq_item?.paid_sum) || 0)
              } else if (bill.fullBillStatus === "signed" && item?.is_item) {
                totalAmount += item?.paid_sum || item?.bill?._boq_item?.paid_sum || 0
              }
            }

            return totalAmount
          }, [bill])

          const totalAccumulatedAmount = item?.bill?.amount_by_signer
            ? item?.bill?.amount_by_signer
            : item?.bill?.approved_amount
              ? item?.bill?.approved_amount
              : item?.bill?.amount
          return item?.is_item ? (
            <div className='text-center text-xs font-bold sm:text-sm'>
              <div className='flex flex-col items-center'>
                <span
                  className={cn(
                    "text-slate-500",
                    billAmounts[item?.id] && "text-primary",
                    totalAccumulatedAmount > item?.amount && "text-red-500",
                  )}
                >
                  {formatCurrency(accumulatedAmount, isHebrew)}
                </span>
              </div>
            </div>
          ) : null
        },
        enableSorting: false,
      },
    )
    return baseColumns
  }, [
    expandedRows,
    rowSelection,
    visibleData,
    hierarchicalData,
    billAmounts,
    discountAmounts,
    excludeDiscountEnabled,
    stateContext,
    isHebrew,
    activeInputId,
    activeDiscountInputId,
    showDiscountedPrices,
    showCurrentBill,
    status,
    translate,
  ])

  const table = useReactTable({
    data: visibleData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
  })

  const sortedData = useMemo(
    () =>
      data.sort((a, b) => {
        const aParts = a.number.split(".").map(Number)
        const bParts = b.number.split(".").map(Number)
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          if (aParts[i] === undefined) return -1
          if (bParts[i] === undefined) return 1
          if (aParts[i] < bParts[i]) return -1
          if (aParts[i] > bParts[i]) return 1
        }
        return 0
      }),
    [data],
  )

  useEffect(() => {
    const allCodes = new Set(hierarchicalData.map((item) => item.number))
    setExpandedRows(allCodes)
  }, [showStatus])

  useEffect(() => {
    if ((data.length < 1 || addBillMutation.isPending) && setIsDisable) {
      setIsDisable(true)
    }
  }, [data, addBillMutation.isPending])

  useEffect(() => {
    if (triggerApi && setTriggerApi) {
      setTriggerApi(false)
      addBillMutation.mutate()
    }
  }, [triggerApi])

  const buttonColorChange =
    data.length < 1 || addBillMutation.isPending || Object.keys(billAmounts).length < 1

  useEffect(() => {
    if (setChangeButtonColor) {
      setChangeButtonColor(buttonColorChange)
    }
  }, [buttonColorChange])

  return (
    <div className='w-full p-1 overflow-x-hidden' dir={isHebrew ? "rtl" : "ltr"}>
      <div className='mb-4 flex flex-col gap-3 md:justify-between xl:flex-row xl:items-end'>
        {context !== "bill-print" ? (
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            <div className='relative w-full sm:w-auto'>
              <Search
                className={cn(
                  "absolute top-2.5 h-4 w-4 text-muted-foreground",
                  isHebrew ? "right-2" : "left-2",
                )}
              />
              <Input
                placeholder={translate("Search") + "..."}
                value={globalFilter ?? ""}
                onChange={(event) => setGlobalFilter(String(event.target.value))}
                className={cn("w-full sm:w-[250px] md:w-[300px]", isHebrew ? "pr-8" : "pl-8")}
              />
            </div>
            <div className='flex items-center gap-2'>
              <Label htmlFor='discount-toggle' className='text-xs sm:text-sm'>
                {translate("Original Prices")}
              </Label>
              <Switch
                id='discount-toggle'
                checked={showDiscountedPrices}
                onCheckedChange={(checked) => setShowDiscountedPrices(checked ? true : false)}
              />
            </div>
          </div>
        ) : null}

        <div className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-end'>
          {!["bill-review", "bill-print"].includes(context) && (
            <>
              <SelectWorkDiaryList
                selectedDairyId={selectedDairyId}
                setSelectedDiaryId={setSelectedDiaryId}
              />
              {/* <Button
                variant={buttonColorChange ? "secondary" : "default"}
                size={"sm"}
                disabled={buttonColorChange}
                onClick={() => {
                  if (stateContext === "bill-create") {
                    if (!window.confirm(translate("are you sure you want to send this bill for approval?"))) return
                    addBillMutation.mutate()
                  }
                  setStateContext("bill-create")
                }}
                loading={addBillMutation.isPending}
                className={cn("text-xs sm:text-sm")}
              >
                <DollarSign className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")} />{" "}
                <span className='hidden sm:inline'>{translate("Create bill")}</span>
                <span className='sm:hidden'>{translate("Bill")}</span>
              </Button> */}
              {context != "bill-create" ? (
                <Button
                  variant='outline'
                  size={"sm"}
                  onClick={() => setShowAddItemDialog(true)}
                  className='text-xs sm:text-sm'
                >
                  <PlusCircle className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")} />
                  <span className='hidden sm:inline'>{translate("Add Item")}</span>
                  <span className='sm:hidden'>{translate("Add")}</span>
                </Button>
              ) : null}
              {canRequestAdditional && Object.keys(rowSelection).length === 1 && (
                <Button
                  variant='outline'
                  size={"sm"}
                  onClick={() => {
                    const selectedId = Object.keys(rowSelection)[0]
                    const item = hierarchicalData.find((item) => item.id == selectedId)
                    if (item) {
                      setSelectedItemForAdditional(item)
                      setShowAdditionalAmountDialog(true)
                    }
                  }}
                  className='text-xs sm:text-sm'
                >
                  <DollarSign className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")} />
                  <span className='hidden sm:inline'>{translate("Request Additional Amount")}</span>
                  <span className='sm:hidden'>{translate("Request")}</span>
                </Button>
              )}
              <Button
                variant='outline'
                className='border-primary text-xs text-primary sm:text-sm'
                size='sm'
                onClick={() => exportToExcel(hierarchicalData, context === "bill-preview" ? "bill-preview" : "main", {}, isHebrew)}
                disabled={data.length < 0}
              >
                <span className='hidden sm:inline'>{translate("Export All to Excel")}</span>
                <span className='sm:hidden'>{translate("Export")}</span>
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  const allCodes = new Set(hierarchicalData.map((item) => item.number))
                  const isAllExpanded = hierarchicalData.every((item) =>
                    expandedRows.has(item.number),
                  )
                  if (isAllExpanded) setExpandedRows(new Set(`${sortedData[0]?.number}`))
                  else setExpandedRows(allCodes)
                }}
                className='text-xs sm:text-sm'
              >
                {hierarchicalData.every((item) => expandedRows.has(item.number)) ? (
                  <>
                    <ChevronRight
                      className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")}
                    />
                    <span className='hidden sm:inline'>{translate("Collapse All")}</span>
                    <span className='sm:hidden'>{translate("Collapse")}</span>
                  </>
                ) : (
                  <>
                    <ChevronDown
                      className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")}
                    />
                    <span className='hidden sm:inline'>{translate("Expand All")}</span>
                    <span className='sm:hidden'>{translate("Expand")}</span>
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className='max-h-[75vh] overflow-auto rounded-md border [&>#wrapper2]:overflow-visible'>
        <Table className='text-xs sm:text-sm'>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className='border-b bg-gray-50'>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "sticky top-0 z-10 border-gray-200 bg-gray-50 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                      isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                    )}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "flex cursor-pointer select-none items-center justify-center gap-1"
                            : "flex items-center justify-center"
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && <ArrowUpDown className='h-3 w-3' />}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => {
              const rowData = row.original
              const isParent = rowData.hasChildren
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    `border-b hover:bg-gray-50 ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-25"
                    } ${isParent ? "font-medium" : ""}`,
                    rowData?.bill?.amount && "border-y border-primary bg-primary/5",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                        isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <div className='flex flex-col items-center justify-between gap-2 py-4 sm:flex-row sm:items-center'>
        <div className='text-xs text-muted-foreground sm:text-sm'>
          {translate("Showing")} {table.getFilteredRowModel().rows.length} {translate("of")}{" "}
          {hierarchicalData.length} {translate("rows")}
        </div>
      </div>

      <>
        <ApproveBoqItems
          showApproveDialog={showApproveDialog}
          setShowApproveDialog={setShowApproveDialog}
          selectedItems={Object.keys(rowSelection)}
          boqItems={data}
          boqId={boqId}
          refetchBoQ={revalidate}
          setSelectedItems={handleRowSelection}
        />

        <AddNewBoQItem
          boqId={boqId}
          refetchBoQ={revalidate}
          setShowAddItemDialog={setShowAddItemDialog}
          showAddItemDialog={showAddItemDialog}
        />
        <RequestAdditionalAmount
          boqItem={selectedItemForAdditional}
          setShowAdditionalAmountDialog={setShowAdditionalAmountDialog}
          showAdditionalAmountDialog={showAdditionalAmountDialog}
          refetchBoQ={revalidate}
        />
      </>
    </div>
  )
}

function BillAmountInput({
  item,
  billAmounts,
  setBillAmounts,
  activeInputId,
  setActiveInputId,
}: any) {
  const { translate } = useTranslator()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (activeInputId === item?.id) {
      inputRef.current?.focus()
    }
  }, [activeInputId, item?.id])

  return (
    <Input
      type='number'
      defaultValue={0}
      ref={inputRef}
      min={0}
      className='w-full border-primary sm:w-[10ch]'
      onFocus={() => setActiveInputId(item?.id)}
      onChange={(e) => {
        const value = Number(e.target.value)

        if (value > item?.amount) {
          toast({
            title: translate("Info"),
            description: translate("Bill item amount is more than tender amount"),
            variant: "info",
          })
        }

        setBillAmounts((prev: any) => {
          // If value is 0 or empty, remove the item from billAmounts
          if (value === 0 || e.target.value === "") {
            const { [item?.id]: _, ...rest } = prev
            return rest
          }
          // Otherwise, set the value
          return {
            ...prev,
            [item?.id]: value,
          }
        })
        setActiveInputId(item?.id)
      }}
      value={billAmounts[item?.id] ?? 0}
    />
  )
}

function AccumulatedTotalCell({ item, billAmounts, currentSum, preview }: any) {
  const { locale } = useTranslator()

  const isHebrew = locale === "he"

  const totalAccumulatedAmount = (Number(billAmounts[item?.id]) || 0) + (Number(item?.paid) || 0)

  const hasExceededEstimate = totalAccumulatedAmount > item?.amount

  if (currentSum) {
    if (!preview) {
      return (
        <div className='flex flex-col items-center'>
          <span
            className={cn(
              "text-slate-500",
              billAmounts[item?.id] && "text-primary",
              hasExceededEstimate && "text-red-500",
            )}
          >
            {item?.bill
              ? formatCurrency(item?.discount_price * item?.bill?.amount, isHebrew)
              : formatCurrency(0, isHebrew)}
          </span>
        </div>
      )
    }
    return (
      <div className='flex flex-col items-center'>
        <span
          className={cn(
            "text-slate-500",
            billAmounts[item?.id] && "text-primary",
            hasExceededEstimate && "text-red-500",
          )}
        >
          {billAmounts[item?.id]
            ? formatCurrency(item?.discount_price * billAmounts[item?.id], isHebrew)
            : formatCurrency(0, isHebrew)}
        </span>
      </div>
    )
  }

  return (
    <div className='flex flex-col items-center'>
      <span className={cn(hasExceededEstimate ? "text-red-500" : "text-green-500")}>
        {formatCurrency((Number(item?.discount_price) || 0) * totalAccumulatedAmount, isHebrew)}
      </span>
    </div>
  )
}
